import React, { useState } from 'react';
import { Code, Coffee, Map, Music, Film } from 'lucide-react';

const About = () => {
  const [activeTab, setActiveTab] = useState('experience');
  
  const experiences = [
    {
      company: "Ease Vertical AI",
      role: "Full Stack AI Product Engineer",
      period: "May 2025 - Sep 2025",
      description: "Led system architecture of AI-native B2B SaaS platform using multi-agent orchestration.",
      highlights: [
        "Architected AI-native B2B SaaS platform using multi-agent orchestration & LLMs",
        "Built robust backend with Python (FastAPI), LangChain, and vector databases",
        "Engineered responsive dashboard using React.js, TypeScript, and TailwindCSS",
        "Deployed microservices on AWS ECS + Docker, optimized with Redis & CloudWatch"
      ],
      techStack: ["Python", "FastAPI", "React", "TypeScript", "LangChain", "AWS", "Docker"]
    },
    {
      company: "Deloitte",
      role: "Front End Engineer",
      period: "Apr 2021 - Aug 2023",
      description: "Developed large-scale enterprise applications for 300K+ users.",
      highlights: [
        "Developed 15+ React/Redux UIs, achieving 95% WCAG accessibility",
        "Mentored 4+ juniors in React ecosystem and led code reviews",
        "Implemented Jest unit testing (95% coverage), reducing bugs by 40%"
      ],
      techStack: ["React", "Redux", "TypeScript", "Jest", "WCAG"]
    },
    {
      company: "Incquet Solutions",
      role: "Full Stack Engineer",
      period: "Jan 2020 - Apr 2021",
      description: "Led development of scalable DMS and AI-powered products.",
      highlights: [
        "Led team of 4 devs, managing production deliveries and CI/CD pipelines",
        "Built AI products from 0-1 using React.js, Python, and LLM integration",
        "Developed scalable Document Management Systems with React, Node.js, Firebase"
      ],
      techStack: ["React", "Python", "Node.js", "Firebase", "LLM Integration"]
    }
  ];

  const education = [
    {
      institution: "Troy University",
      degree: "Masters in Computer Science",
      period: "Aug 2023 - Dec 2024",
      description: "Focused on advanced computer science topics such as AI, Machine Learning, and Data Structures.",
      highlights: [
        "Specialized in Machine Learning and AI applications",
        "Completed research project on neural networks for image recognition",
        "Active member of the Computer Science Graduate Association"
      ],
      courses: ["Advanced Algorithms", "Machine Learning", "Distributed Systems", "Computer Vision"]
    },
    {
      institution: "Smt. Kashibai Nawale College Of Engineering",
      degree: "Bachelors in Computer Science",
      period: "2016 - 2020",
      description: "Studied the fundamentals of computer science and software engineering, with hands-on experience in coding and project development.",
      highlights: [
        "Developed a smart attendance system for final year project",
        "Participated in multiple hackathons and coding competitions"
      ],
      courses: ["Data Structures", "Object-Oriented Programming", "Database Management", "Software Engineering"]
    }
  ];

  const personalInfo = {
    interests: [
      { name: "Coding Side Projects", icon: <Code className="w-5 h-5" /> },
      { name: "Coffee Enthusiast", icon: <Coffee className="w-5 h-5" /> },
      { name: "Hiking Adventures", icon: <Map className="w-5 h-5" /> },
      { name: "Music Production", icon: <Music className="w-5 h-5" /> },
      { name: "Film Photography", icon: <Film className="w-5 h-5" /> }
    ],
    funFacts: [
      "I can speak 5 languages",
      "Amateur coffee roaster with a home setup",
      "Ran a half marathon in 2022",
      "Built a custom mechanical keyboard from scratch"
    ]
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'experience':
        return (
          <div className="space-y-8 py-4">
            {experiences.map((exp, index) => (
              <div key={index} className="group relative pl-8 border-l border-gray-200 hover:border-blue-500 transition-colors duration-300">
                  {/* Timeline dot */}
                  <div className="absolute left-0 top-0 -translate-x-[5px] w-2.5 h-2.5 rounded-full bg-gray-200 group-hover:bg-blue-500 transition-colors"></div>
                  
                  {/* Content */}
                  <div className="space-y-4">
                      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2">
                        <div>
                            <h3 className="text-xl font-bold text-gray-900">{exp.company}</h3>
                            <p className="text-blue-600 font-medium">{exp.role}</p>
                        </div>
                        <span className="text-xs font-mono font-medium text-gray-500 bg-gray-100 px-2 py-1 rounded">{exp.period}</span>
                      </div>
                      
                      <p className="text-gray-600 leading-relaxed">{exp.description}</p>
                      
                      <ul className="space-y-2">
                        {exp.highlights.map((highlight, i) => (
                          <li key={i} className="flex items-start text-sm text-gray-600">
                            <span className="mr-2 text-gray-400">•</span>
                            {highlight}
                          </li>
                        ))}
                      </ul>
                      
                      <div className="flex flex-wrap gap-2 pt-2">
                        {exp.techStack.map((tech, i) => (
                          <span key={i} className="px-2 py-1 bg-gray-50 border border-gray-100 rounded text-xs text-gray-600 font-medium">
                            {tech}
                          </span>
                        ))}
                      </div>
                  </div>
              </div>
            ))}
          </div>
        );
        
      case 'education':
        return (
          <div className="space-y-8 py-4">
            {education.map((edu, index) => (
                <div key={index} className="group relative pl-8 border-l border-gray-200 hover:border-purple-500 transition-colors duration-300">
                    <div className="absolute left-0 top-0 -translate-x-[5px] w-2.5 h-2.5 rounded-full bg-gray-200 group-hover:bg-purple-500 transition-colors"></div>
                    
                    <div className="space-y-4">
                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2">
                            <div>
                                <h3 className="text-xl font-bold text-gray-900">{edu.institution}</h3>
                                <p className="text-purple-600 font-medium">{edu.degree}</p>
                            </div>
                            <span className="text-xs font-mono font-medium text-gray-500 bg-gray-100 px-2 py-1 rounded">{edu.period}</span>
                        </div>
                        
                        <p className="text-gray-600 leading-relaxed">{edu.description}</p>
                        
                        <div>
                           <h5 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Highlights</h5>
                           <ul className="space-y-1">
                                {edu.highlights.map((highlight, i) => (
                                <li key={i} className="flex items-start text-sm text-gray-600">
                                    <span className="mr-2 text-gray-400">•</span>
                                    {highlight}
                                </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            ))}
          </div>
        );
        
      case 'personal':
        return (
          <div className="py-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Interests */}
              <div className="glass p-6 rounded-xl">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <Coffee className="w-5 h-5 text-gray-500" /> Interests
                </h3>
                <div className="grid grid-cols-1 gap-3">
                  {personalInfo.interests.map((interest, index) => (
                    <div key={index} className="flex items-center p-3 rounded-lg bg-gray-50/50 hover:bg-gray-100 transition-colors">
                      <div className="mr-3 text-gray-600">
                        {interest.icon}
                      </div>
                      <span className="text-gray-700 font-medium text-sm">{interest.name}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Fun Facts */}
              <div className="glass p-6 rounded-xl">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <Code className="w-5 h-5 text-gray-500" /> Fun Facts
                </h3>
                <ul className="space-y-3">
                  {personalInfo.funFacts.map((fact, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 text-xs font-bold mt-0.5">
                        {index + 1}
                      </span>
                      <span className="text-gray-700 text-sm leading-relaxed">{fact}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        );
        
      default:
        return null;
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-4 md:p-8 font-sans">
      {/* Header */}
      <div className="mb-10">
        <h1 className="text-4xl font-bold mb-4 tracking-tight text-gray-900">About Me</h1>
        <p className="text-lg text-gray-600 max-w-2xl leading-relaxed">
          Full Stack Developer with a passion for creating elegant solutions via clean code. 
          Bringing technical expertise and creative thinking to every project.
        </p>
      </div>
      
      {/* Tab Navigation */}
      <div className="mb-8 border-b border-gray-200">
        <div className="flex space-x-1 relative">
          {['experience', 'education', 'personal'].map((tab) => {
             const isActive = activeTab === tab;
             return (
                <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-6 py-3 font-medium text-sm transition-all relative capitalize ${
                    isActive 
                    ? 'text-black border-b-2 border-black' 
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                >
                {tab}
                </button>
             );
          })}
        </div>
      </div>
      
      {/* Tab Content */}
      {/* Tab Content */}
      <div>
          {renderTabContent()}
      </div>
    </div>
  );
};

export default About;

